import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../services/api'
import { useImageManager } from './useImageManager'
import { useAuth } from './useAuth'

/**
 * Hook para gerenciar o fluxo do assistente de cotação
 * 
 * Single Responsibility: Orquestra o fluxo de extração → preview → criação
 */
export function useAssistantFlow() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profile } = useAuth()
  
  // Estados do fluxo
  const [step, setStep] = useState('chat') // chat | preview | images
  const [preview, setPreview] = useState(null)
  const [clientName, setClientName] = useState(
    new URLSearchParams(window.location.search).get('contact_name') || ''
  )
  const [contactId, setContactId] = useState(new URLSearchParams(window.location.search).get('contact_id') || null)
  const contactNameFetched = useRef(false)

  // Buscar nome do contato da nascente (GET /crm/contacts/:id → get_display_name)
  // Fallback: se contact_name não veio na URL, busca via API
  useEffect(() => {
    if (!contactId || contactNameFetched.current) return
    if (clientName) return // já tem nome da URL — não precisa buscar
    contactNameFetched.current = true
    api.getContact(contactId).then(contact => {
      if (contact) {
        setClientName(contact.display_name || '')
      }
    }).catch(() => {})
  }, [contactId])
  
  // Estados de loading/erro
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Estado de correção
  const [correction, setCorrection] = useState('')
  const [refining, setRefining] = useState(false)
  
  // Estados de imagens do destino
  const [imageUrl1, setImageUrl1] = useState('')
  const [imageUrl2, setImageUrl2] = useState('')
  const [existingImages, setExistingImages] = useState({ img1: null, img2: null })
  
  // Hook de imagens de blocos
  const {
    hotelImages,
    carImages,
    tourImages,
    handleImageChange,
    loadImagesForBlocks,
    saveAllImages,
    validateAllImages
  } = useImageManager()

  // Enviar para extração
  const handleSubmit = async (prompt, files) => {
    if (!prompt.trim() || files.length === 0) return

    setLoading(true)
    setError(null)

    try {
      const result = await api.assistantExtract(prompt, files)

      if (result.success) {
        setPreview(result.data)
        setStep('preview')
      } else {
        setError(result.error || 'Erro ao processar')
      }
    } catch (err) {
      setError(err.message || 'Erro ao processar')
    } finally {
      setLoading(false)
    }
  }

  // Enviar correção
  const handleCorrection = async () => {
    if (!correction.trim() || !preview) return

    setRefining(true)
    setError(null)

    try {
      const result = await api.assistantRefine(preview, correction)

      if (result.success) {
        setPreview(result.data)
        setCorrection('')
      } else {
        setError(result.error || 'Erro ao corrigir')
      }
    } catch (err) {
      setError(err.message || 'Erro ao corrigir')
    } finally {
      setRefining(false)
    }
  }

  // Confirmar preview - inicia fluxo de imagens
  const handleConfirmPreview = async () => {
    if (!preview || !clientName.trim()) {
      setError('Preencha o nome do cliente')
      return
    }

    if (!contactId) {
      setError('Selecione o cliente na lista do CRM. Digite o nome e clique na sugestão.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const destination = preview.destination || ''

      // Busca imagens do destino no banco
      const destImages = await api.getDestinationImages(destination)

      const hasImg1 = !!(destImages?.destination_image_url?.trim())
      const hasImg2 = !!(destImages?.destination_image_url_2?.trim())

      setExistingImages({
        img1: hasImg1 ? destImages.destination_image_url : null,
        img2: hasImg2 ? destImages.destination_image_url_2 : null
      })

      if (hasImg1) setImageUrl1(destImages.destination_image_url)
      if (hasImg2) setImageUrl2(destImages.destination_image_url_2)

      // Busca imagens dos blocos no banco
      const loadedImages = await loadImagesForBlocks(preview.blocks || [])

      const needsDestinationImages = !hasImg1 || !hasImg2
      const allBlockImagesValid = validateAllImages(
        loadedImages.hotels,
        loadedImages.cars,
        loadedImages.tours
      )

      // Se tem todas as imagens, cria direto
      if (!needsDestinationImages && allBlockImagesValid) {
        await createQuote(
          destImages.destination_image_url,
          destImages.destination_image_url_2,
          loadedImages.hotels,
          loadedImages.cars,
          loadedImages.tours
        )
        return
      }

      // Se falta alguma, abre modal de imagens
      setStep('images')
    } catch (err) {
      setError('Erro ao carregar imagens: ' + (err?.message || 'erro desconhecido'))
    } finally {
      setLoading(false)
    }
  }

  // Validar imagens antes de criar
  const validateImages = () => {
    if (!String(imageUrl1 || '').trim() || !String(imageUrl2 || '').trim()) return false
    return validateAllImages()
  }

  // Handler do modal de imagens
  const handleImagesSubmit = async () => {
    if (!validateImages()) {
      setError('Preencha todas as URLs de imagem')
      return
    }

    setLoading(true)
    try {
      await saveAllImages()
      await api.saveDestinationImages(preview?.destination, imageUrl1, imageUrl2)
      await createQuote(imageUrl1, imageUrl2, hotelImages, carImages, tourImages)
    } catch (err) {
      setError('Erro ao criar cotação: ' + (err?.message || 'erro desconhecido'))
      setLoading(false)
    }
  }

  // Criar cotação final
  const createQuote = async (img1, img2, hotelImgs, carImgs, tourImgs) => {
    if (!profile?.seller_id) {
      setError('Perfil não carregado. Faça login novamente.')
      setLoading(false)
      return
    }

    if (!contactId) {
      setError('Contato não vinculado. Selecione da lista do CRM.')
      setLoading(false)
      return
    }

    const blocks = preview?.blocks || []

    // Valida que todos os blocos têm data
    const blocksWithoutDate = blocks.filter(b => !b.date)
    if (blocksWithoutDate.length > 0) {
      const tipos = blocksWithoutDate.map(b => b.title || b.type).join(", ")
      setError(`Informe a data de todos os itens antes de salvar. Sem data: ${tipos}`)
      setLoading(false)
      return
    }

    // Adiciona imagens aos blocos
    const blocksWithImages = blocks.map((block) => {
      const imgs =
        block.type === 'hotel' ? hotelImgs?.[block.title] :
          block.type === 'car' ? carImgs?.[block.title] :
            block.type === 'tour' ? tourImgs?.[block.title] :
              null

      if (imgs) {
        return {
          ...block,
          image_url: imgs.img1,
          details: {
            ...(block.details || {}),
            image_url_2: imgs.img2,
            image_url_3: imgs.img3
          }
        }
      }

      return block
    })

    // Calcula start_date/end_date dos blocos se não vier do preview
    const blockDates = blocksWithImages
      .map(b => b.date)
      .filter(Boolean)
      .sort()
    const startDate = preview?.start_date || blockDates[0] || new Date().toISOString().split('T')[0]
    const endDate = preview?.end_date || blockDates[blockDates.length - 1] || startDate

    const quote = await api.createQuoteFromExtracted({
      client_name: clientName,
      contact_id: contactId,
      destination: preview?.destination,
      destination_image_url: img1,
      destination_image_url_2: img2,
      start_date: startDate,
      end_date: endDate,
      blocks: blocksWithImages,
      total_price: preview?.total_price,
      seller_id: profile.seller_id,
      is_returning_client: false,
      adults: preview?.adults || 1,
      children: preview?.children || 0,
      infants: preview?.infants || 0
    })

    navigate(`/app/cotacao/${quote.id}`)
  }

  // Reset para estado inicial
  const resetFlow = () => {
    setStep('chat')
    setPreview(null)
    setCorrection('')
    setClientName('')
    setContactId(null)
    setError(null)
  }

  return {
    // Estados
    step,
    preview,
    clientName,
    contactId,
    loading,
    error,
    correction,
    refining,
    profile,
    
    // Estados de imagens
    imageUrl1,
    imageUrl2,
    existingImages,
    hotelImages,
    carImages,
    tourImages,
    
    // Setters
    setStep,
    setClientName,
    setContactId,
    setCorrection,
    setImageUrl1,
    setImageUrl2,
    setError,
    
    // Handlers
    handleSubmit,
    handleCorrection,
    handleConfirmPreview,
    handleImagesSubmit,
    handleImageChange,
    validateImages,
    resetFlow
  }
}
